<template>
    <div class="lead-form-modal-dst">
        <dk-content-wizard>
            <template #header="{ currentStepPosition, previousStep, showPrev }">
                <div
                    class="modal__header-dst tw-flex tw-items-center tw-justify-between tw-relative tw-shadow-md-down tw-p-6"
                >
                    <dk-button
                        :class="!showPrev ? 'tw-opacity-0' : null"
                        variant="ghost"
                        type="button"
                        @click="previousStep"
                    >
                        <dk-icon name="ds-chevron-left" size="s" />
                    </dk-button>
                    <p class="headline-sm">
                        {{ getCurrentStepTitle(currentStepPosition) }}
                    </p>
                    <dk-button variant="ghost" type="button" @click="hideModal()">
                        <dk-icon name="ds-close-default" size="s" />
                    </dk-button>
                </div>
            </template>

            <template #default>
                <dk-content-step v-if="hasVehicles">
                    <div class="modal__body-dst tw-px-6 tw-pt-2 tw-pb-6">
                        <div class="modal__body-dst-step tw-mb-6">
                            <h2 class="subtitle-sm tw-font-semibold tw-text-basic">
                                {{ titleText }}
                            </h2>
                            <p v-if="subtitle" class="caption tw-text-basic" v-html="subtitle"></p>
                        </div>

                        <div class="modal__body-dst-vehicles tw-flex tw-flex-col tw-gap-6">
                            <div
                                v-for="(vehicle, i) in vehicles"
                                :key="`vehicle-${i}-${vehicle.modelId}`"
                                class="modal__body-dst-vehicle"
                            >
                                <h2 class="modal__body-dst-vehicle-model body-sm">
                                    {{ vehicle.title }}
                                </h2>
                                <h3 class="modal__body-dst-vehicle-version caption tw-font-semibold tw-mb-2">
                                    {{ vehicle.versionName }}
                                </h3>

                                <div class="modal__body-offers tw-flex tw-flex-col dst">
                                    <button
                                        v-for="(offer, j) in vehicle.offers"
                                        :key="`offer-${vehicle.modelId}-${j}`"
                                        class="modal__body-offer tw-flex tw-items-center tw-rounded tw-border tw-border-solid tw-border-border-color tw-p-4 tw-cursor-pointer lg:hover:tw-bg-secondary-light tw-duration-300"
                                        :class="{
                                            'tw-border-brand-primary tw-text-brand-primary tw-bg-primary-light':
                                                offer.selected,
                                        }"
                                        @click.stop="onOfferChange(vehicle.offers, j)"
                                    >
                                        <input
                                            :id="`offer-${vehicle.modelId}-${j}`"
                                            class="tw-pointer-events-none"
                                            :class="{
                                                '!tw-border-brand-primary !tw-text-brand-primary': offer.selected,
                                            }"
                                            type="radio"
                                            :value="offer.label"
                                            :checked="offer.selected"
                                            :disabled="!offer.active"
                                            :name="`offers-${vehicle.modelId}`"
                                        >
                                        <span class="modal__body-offer-price-wrapper tw-flex tw-flex-col">
                                            <span class="modal__body-offer-container tw-flex">
                                                <span
                                                    v-if="offer.pricePrefix"
                                                    class="modal__body-offer-price-prefix tw-mr-2"
                                                >{{ offer.pricePrefix }}</span>
                                                <label
                                                    :for="`offer-${vehicle.modelId}-${j}`"
                                                    class="modal__body-offer-price tw-text-basic tw-pointer-events-none tw-font-semibold"
                                                >{{ offer.finalPrice || offer.originalPrice }}</label>
                                                <span
                                                    v-if="offer.priceSuffix"
                                                    class="modal__body-offer-price-suffix tw-ml-2 micro tw-text-base-subtle"
                                                >{{ offer.priceSuffix }}</span>
                                            </span>
                                            <span class="modal__body-offer-services caption tw-text-base-subtle">
                                                <span
                                                    v-if="
                                                        isOfferType(offer, 'contract_hire') &&
                                                            offer.servicesCount &&
                                                            offer.servicesCount.length
                                                    "
                                                    class="modal__body-offer-services-count"
                                                >
                                                    {{
                                                        $tc('lead-form-modal.services-count', offer.servicesCount, {
                                                            n: offer.servicesCount,
                                                        })
                                                    }}
                                                </span>
                                                <span
                                                    v-else-if="
                                                        !offer.active &&
                                                            isOfferType(offer, 'listing') &&
                                                            !offer.originalPrice
                                                    "
                                                    class="modal__body-offer-no-services tw-text-feedback-danger-border"
                                                >
                                                    {{ $t('lead-form-modal.no-partner') }}
                                                </span>
                                                <span
                                                    v-else-if="offer.originalPrice"
                                                    class="modal__body-offer-original-price tw-line-through"
                                                >{{ offer.originalPrice }}</span>
                                            </span>
                                        </span>
                                        <span class="modal__body-offer-type tw-ml-auto">
                                            <dk-badge
                                                class="dk-offer-card__badge tw-w-fit tw-visible lg:tw-hidden"
                                                size="small"
                                                :type="offer.selected ? 'blue' : 'light-blue'"
                                            >
                                                <span class="dk-offer-card__badge-text tw-mr-2">{{
                                                    offer.type.text
                                                }}</span>
                                                <dk-icon name="ds-tag-price-filled" size="xs" />
                                            </dk-badge>
                                            <dk-badge
                                                class="dk-offer-card__badge tw-w-fit tw-hidden lg:tw-inline-flex"
                                                size="default"
                                                :type="offer.selected ? 'blue' : 'light-blue'"
                                            >
                                                <span class="dk-offer-card__badge-text tw-mr-2">{{
                                                    offer.type.text
                                                }}</span>
                                                <dk-icon name="ds-tag-price-filled" size="xs" />
                                            </dk-badge>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </dk-content-step>
                <dk-content-step>
                    <div class="modal__body-dst tw-p-6">
                        <lead-form-wrapper
                            ref="leadFormWrapper"
                            :form-id="formId"
                            :form-data="formData"
                            @after-form-validation="onLeadFormSubmit"
                        />
                    </div>
                </dk-content-step>
            </template>

            <template #footer="{ totalSteps, currentStepPosition, nextStep, showNext, stepStatusText }">
                <div v-if="showNext" class="tw-p-6">
                    <dk-button
                        class="!tw-h-12 tw-w-full !tw-text-lg"
                        type="button"
                        @click="handleNextStep(totalSteps, currentStepPosition, nextStep)"
                    >
                        {{ $t('lead-form-modal.next-step') }} ({{ stepStatusText }})
                    </dk-button>
                </div>
            </template>
        </dk-content-wizard>
    </div>
</template>
<script>
import isFunction from 'lodash/isFunction';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { create, createMultiple } from '@v4/services/api/lead-form';
import { navigateTo } from '@v4/http/request';
import { getPrismImageUrl } from '@v4/common/utils/image';
import DkContentWizard from '@v4/common/components/molecules/DkContentWizard';
import DkContentStep from '@v4/common/components/atoms/DkContentStep';
import LeadFormWrapper from '@v4/common/components/organisms/LeadFormWrapper';

/**
 * LeadFormModal component.
 *
 * @version 4.0.0
 * @example ./examples/LeadFormModal.md
 */
export default {
    name: 'LeadFormModal',
    components: {
        LeadFormWrapper,
        DkContentWizard,
        DkContentStep,
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
        }),
        DkBadge: () => ({
            component: import(/* webpackChunkName: "DkBadgeDST-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'),
        }),
    },
    mixins: [dkModalAware],
    props: {
        /**
         * Form ID.
         */
        formId: {
            type: String,
            default: '',
        },
        /**
         * Form data to send to HTTP request.
         */
        formData: {
            type: Object,
            default: null,
        },
        /**
         * Modal title.
         */
        title: {
            type: String,
            default: '',
        },
        /**
         * Modal subtitle. HTML it's allowed.
         */
        subtitle: {
            type: String,
            default: '',
        },
        /**
         * Vehicle list.
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Function that triggers when the button to step to lead form is clicked
         */
        onDiscoverPrice: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            vehicles: this.items || [],
        };
    },
    computed: {
        hasVehicles() {
            return !!this.vehicles.length;
        },
        titleText() {
            if (this.title) {
                return this.title;
            }

            if (this.hasVehicles) {
                return this.$t('lead-form-modal.your-selection');
            }

            return this.$t('lead-form-modal.request-a-quote');
        },
        leadFormComponent() {
            return this.$refs.leadFormWrapper;
        },
    },
    methods: {
        getPrismImageUrl,
        handleNextStep(totalSteps, currentStepPosition, cb) {
            if (totalSteps - 1 === currentStepPosition) {
                // The user clicks to go to the final step which is the lead form
                if (this.onDiscoverPrice && typeof this.onDiscoverPrice === 'function') {
                    this.onDiscoverPrice();
                }
            }

            if (cb && typeof cb === 'function') {
                cb();
            }
        },
        onLeadFormSubmit({ data }) {
            this.setErrorMessage('');
            let promise = null;

            if (this.hasVehicles) {
                promise = createMultiple(
                    data,
                    this.vehicles.map((vehicle) => {
                        const offer = vehicle.offers.find((item) => item.selected) || {};

                        return {
                            ...vehicle,
                            promotionId: offer.promotionId || null,
                            projectId: offer.projectId || null,
                            poId: offer.poId || null,
                        };
                    })
                );
            } else {
                promise = create(data);
            }

            return promise
                .then(({ data }) => {
                    if (data.data && data.data.thankYouPageUrl) {
                        navigateTo(data.data.thankYouPageUrl);
                    } else {
                        this.stopSubmitting();
                    }
                })
                .catch((error) => {
                    this.stopSubmitting();

                    if (error.response && error.response.data.data && error.response.data.data.message) {
                        this.setErrorMessage(error.response.data.data.message);
                    } else {
                        this.setErrorMessage(this.$t('api-error-handler.message.error'));
                    }
                });
        },
        setErrorMessage(msg) {
            if (!this.leadFormComponent || !isFunction(this.leadFormComponent.setLeadFormErrorMessage)) {
                return;
            }

            this.leadFormComponent.setLeadFormErrorMessage(msg);
        },
        stopSubmitting() {
            if (!this.leadFormComponent || !isFunction(this.leadFormComponent.setLeadFormIsBusy)) {
                return;
            }

            this.leadFormComponent.setLeadFormIsBusy(false);
        },
        onOfferChange(offers, i) {
            const offer = offers[i];

            if (!offer.active) {
                return;
            }

            offers.forEach((item) => this.$set(item, 'selected', false));
            this.$set(offer, 'selected', true);
        },
        isOfferType(offer = {}, type) {
            return offer.type && offer.type.slug.toLowerCase() === type;
        },
        getCurrentStepTitle(currentStepPosition) {
            switch (currentStepPosition) {
                case 1:
                    return this.$t('lead-form-modal.step-1-title');
                case 2:
                    return this.$t('lead-form-modal.step-2-title');
                default:
                    return '';
            }
        },
    },
};
</script>
